---
title: The slow concept phase
date: 2026-03-08
author: Brian Ellison
category: Process
excerpt: Why we spend months on concept before a single drawing is produced — and why most of the value of a project is created during that quiet stretch.
placeholder: true
---

Of every project hour we bill, the slowest hour is the one **before** a drawing exists.

It is also the most valuable.

## What happens in the concept phase

We don't sketch. We don't pick fixtures. We don't quote square footage. The concept phase is conversation, photographs, walks through the site at different times of day, samples passed across the dining table, sketches that get torn in half on Tuesday and reassembled differently on Friday.

Concretely, the concept phase produces three things:
- A **point of view**. Two sentences about what this house, restaurant, or room is *for*. Not the program; the **disposition**.
- A **material vocabulary**. Six to nine words: the materials and finishes the project will deploy. No more.
- A **lighting plan**. Drawn long before any walls are.

When all three exist on paper, design development starts.

<div class="pull-quote">

"A project that is rushed in concept will be late in construction. A project that is patient in concept will be on time and on budget. We have never seen this fail."

</div>

## Why this works

Because the **wrong answer to a hard design question is cheap to discover during concept** and **expensive to discover during construction**.

Discovering during concept that an east-facing window won't work for a reading nook costs us a sketch.

Discovering the same fact during construction costs the client thirty thousand dollars and four weeks of trade rescheduling.

## Why most studios skip it

Two reasons.

The first is **cash flow**. The concept phase generates few deliverables. Clients sometimes feel they are paying for "talk." Studios under cash pressure feel pressure to produce — anything — and move past concept too quickly.

The second is **practitioner fatigue**. Concept is the most intellectually demanding stretch of a project. It is much easier to specify a sconce than to sit with the question of whether the sconce should exist.

We hold the line on concept because the projects we are most proud of are projects where the concept was slow.

*A practice note. More to follow.*
